'use client'

import { useState } from 'react'

import { motion } from 'framer-motion'

import objectivesIcon from '../assets/objectives-icon.png'
import greenCheckIcon from '../assets/green-check.png'
import { usePassObjectiveClaim } from '../hooks/usePassObjectiveClaim'
import {
  collapsedObjectives,
  objectiveLabel,
  objectiveRewardItems
} from '../lib/pass-ui'
import type { Pass, PassObjective, PassObjectiveResourceType } from '../types/pass'

import { AppLink } from '@/shared/lib/router'

import { PassRewardRevealModal } from './PassRewardRevealModal'

const RESOURCE_ROUTE: Record<PassObjectiveResourceType, string> = {
  Mission: '/missoes',
  Journey: '/jornadas',
  Level: '/jornadas',
  Document: '/biblioteca'
}

interface PassObjectivesCardProps {
  pass: Pass
}

export function PassObjectivesCard({ pass }: PassObjectivesCardProps) {
  const claim = usePassObjectiveClaim()
  const [revealed, setRevealed] = useState<PassObjective | null>(null)

  const objectives = collapsedObjectives(pass)

  if (objectives.length === 0) return null

  const objectivesTitle =
    objectives.length === 1
      ? 'Objetivo para avançar'
      : 'Objetivos para avançar'

  function rewardSlot(objective: PassObjective) {
    if (objective.coin_reward <= 0) return null

    if (objective.status === 'delivered') {
      return (
        <span className="col-start-2 flex shrink-0 items-center gap-1.5 text-[0.95rem] font-black tracking-[-0.03em] text-[#1F9D57] min-[390px]:col-start-auto min-[390px]:justify-self-end sm:text-[1.35rem]">
          <img src={greenCheckIcon} alt="" className="size-5 shrink-0 sm:size-7" />
          Resgatado
        </span>
      )
    }

    if (objective.status === 'available') {
      const pending = claim.isPending && claim.variables?.id === objective.id
      return (
        // Mesma linguagem de resgate do tier: o botão pulsa enquanto o POST
        // está em voo e a revelação abre no sucesso.
        <motion.button
          type="button"
          disabled={pending}
          onClick={() => claim.mutate(objective, { onSuccess: setRevealed })}
          animate={pending ? { scale: [1, 1.06, 1] } : { scale: 1 }}
          transition={
            pending
              ? { duration: 0.7, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.2 }
          }
          className="col-start-2 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-pass-cta px-4 py-2 text-[0.95rem] font-black tracking-[-0.03em] text-white transition-colors hover:bg-pass-cta-hover disabled:opacity-60 min-[390px]:col-start-auto min-[390px]:justify-self-end sm:text-[1.35rem]"
        >
          {pending ? 'Resgatando…' : `Resgatar +${objective.coin_reward}`}
          {!pending && (
            <img src="/icons/coin.svg" alt="moedas" className="size-5 shrink-0 sm:size-7" />
          )}
        </motion.button>
      )
    }

    return (
      <span className="col-start-2 flex shrink-0 items-center gap-1.5 text-[1rem] font-black tracking-[-0.04em] text-[#2A255A] min-[390px]:col-start-auto min-[390px]:justify-self-end sm:text-[1.55rem]">
        +{objective.coin_reward}
        <img src="/icons/coin.svg" alt="moedas" className="size-5 shrink-0 sm:size-7" />
      </span>
    )
  }

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-[linear-gradient(180deg,var(--color-white)_0%,var(--pass-card-tint)_100%)] shadow-[0_18px_40px_rgba(10,3,31,0.14)]">
      <div className="flex items-center gap-3 px-5 pb-4 pt-5 sm:px-7 sm:pb-5 sm:pt-6">
        <img src={objectivesIcon} alt="" className="size-10 shrink-0 sm:size-11" />
        <h3 className="text-[1.4rem] font-black tracking-[-0.05em] text-[#221B4B] sm:text-[2.2rem]">
          {objectivesTitle}
        </h3>
      </div>

      <ul className="divide-y divide-[#E8DEFB]">
        {objectives.map(objective => (
          <li
            key={objective.id}
            className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-5 py-4 min-[390px]:grid-cols-[auto_minmax(0,1fr)_auto] sm:px-7 sm:py-5"
          >
            {objective.completed ? (
              <img
                src={greenCheckIcon}
                alt="Objetivo concluído"
                className="size-9 shrink-0 sm:size-10"
              />
            ) : (
              <span
                className="size-9 shrink-0 rounded-full border-[3px] border-pass-ring bg-white sm:size-10"
                aria-hidden
              />
            )}

            <AppLink
              href={RESOURCE_ROUTE[objective.resource_type] ?? '/jornadas'}
              className="min-w-0 text-[1rem] font-bold tracking-[-0.03em] text-pass-ink transition-colors hover:text-pass-cta sm:text-[1.7rem]"
            >
              {objectiveLabel(objective)}
            </AppLink>

            {rewardSlot(objective)}
          </li>
        ))}
      </ul>

      {revealed && objectiveRewardItems(revealed).length > 0 ? (
        <PassRewardRevealModal
          items={objectiveRewardItems(revealed)}
          description="Objetivo do passe concluído."
          onClose={() => setRevealed(null)}
        />
      ) : null}
    </section>
  )
}
